// miniapp/src/components/admin/AnnouncementsScreen.tsx
//
// Phase 39 (Announcements / Broadcast MVP) — the level>=2 admin announcements screen.
//
// Mirrors CannedAdminScreen.tsx structure exactly:
//   - List view: past/running broadcasts with status chips + sent/total progress
//   - Compose sheet: plain-text message (D-01), four-filter audience builder (D-03),
//     live "Estimated recipients: N" from POST /preview, send-test-to-chat-id (D-02)
//   - Typed-count confirm dialog (D-04): for large sends (>= TYPED_CONFIRM_THRESHOLD)
//     the Send button is disabled until the user types the recipient count; small
//     sends send on a plain tap
//   - Detail view: live progress bars fed by broadcast_progress socket event
//     (subscribe to useTicketWebSocket ticketId='stats')
//
// The frontend level>=2 hide (in App.tsx / TopNavbar) is UX only — the real
// enforcement is the server-side requireLevel(2) gate (39-03).

import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import {
  ChevronLeft,
  Megaphone,
  Plus,
  Loader2,
  X,
  RefreshCw,
  Ban,
  Download,
  PlayCircle,
  Users,
  Upload,
} from 'lucide-react';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useSimVerifierSubLabels } from '../../hooks/useSimVerifierSubLabels';
import { useTicketWebSocket } from '../../hooks/useTicketWebSocket';
import { apiClient } from '../../services/api';
import { bridgeService } from '../../services/bridge';
import type {
  AudienceFilter,
  Broadcast,
  BroadcastDetail,
  BroadcastProgressEvent,
  UploadSummary,
} from '../../types';

// ── Constants ────────────────────────────────────────────────────────────────
const SERVICE_TYPE_OPTIONS = [
  { value: 'sim_verifier', label: 'SIM Verifier' },
  { value: 'dg_wallet', label: 'DG Wallet' },
  { value: 'other', label: 'Other' },
];

const inputClass =
  'w-full rounded-xl border border-border-primary bg-white px-3 py-2.5 text-sm text-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-600/40';

// Phase 41: recipient count at/above which Step 3 requires the typed-count
// confirm. Small sends (below this) send on a plain tap — the friction gate is
// reserved for large blasts where a mistaken audience is costly.
const TYPED_CONFIRM_THRESHOLD = 20;

// Phase 41/42 (Step 1 preview): extract a YouTube video id from a link.
// Mirrors the server's extractYouTubeId (broadcastFormat.ts) so the composer
// preview matches what the dispatcher actually sends. Only YouTube links get a
// native thumbnail in the delivered message (non-YouTube links have their web
// preview disabled server-side), so the preview keys the thumbnail on this too.
function extractYouTubeId(url: string): string | null {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = u.pathname.slice(1).split('/')[0] ?? '';
      return /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      if (u.pathname === '/watch') {
        const id = u.searchParams.get('v') ?? '';
        return /^[\w-]{11}$/.test(id) ? id : null;
      }
      const m = u.pathname.match(/^\/(?:embed|shorts|v)\/([\w-]{11})/);
      if (m) return m[1] ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

/** Real YouTube thumbnail URL for the composer preview. hqdefault (480×360) is
 *  always present for any valid id, so no fallback fetch is needed client-side. */
function youTubeThumbUrl(id: string): string {
  return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
}

/** Race a boolean-returning promise against a timeout so a hung native bridge
 *  call (e.g. saveFile whose native side never calls back) can never leave a
 *  loading spinner stuck. Resolves `false` on timeout (a soft failure). */
function withTimeout(p: Promise<boolean>, ms = 20000): Promise<boolean> {
  return Promise.race([
    p,
    new Promise<boolean>((resolve) => setTimeout(() => resolve(false), ms)),
  ]);
}

// Copy text to the clipboard. navigator.clipboard is often unavailable/blocked
// in the SuperApp iOS webview, so fall back to a hidden-textarea execCommand.
async function copyToClipboard(text: string): Promise<boolean> {
  // Native SuperApp bridge first — web clipboard APIs are blocked in the webview.
  if (await bridgeService.copyToClipboard(text)) return true;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to execCommand */
  }
  try {
    // iOS-compatible: element must be readonly + a real Range selection, not
    // just .select() (which iOS ignores on off-screen inputs).
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.contentEditable = 'true';
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    const range = document.createRange();
    range.selectNodeContents(ta);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ── Status chip colours ───────────────────────────────────────────────────────
function statusChip(status: Broadcast['status']): { label: string; className: string } {
  switch (status) {
    case 'sending':
      return { label: 'Sending', className: 'bg-blue-100 text-blue-700' };
    case 'completed':
      return { label: 'Completed', className: 'bg-green-100 text-green-700' };
    case 'cancelled':
      return { label: 'Cancelled', className: 'bg-gray-100 text-gray-600' };
    case 'queued':
      return { label: 'Queued', className: 'bg-yellow-100 text-yellow-700' };
    default:
      return { label: 'Draft', className: 'bg-gray-100 text-gray-500' };
  }
}

// ── Phase 42 history-view helpers ─────────────────────────────────────────────
// The list view is a date-grouped history timeline. These pure helpers derive
// the per-entry audience label, the timeline node color, and the date labels
// from the existing broadcast row (no new data).

/** Audience label for a history row, from the stored audience_filter. */
function describeAudience(
  filter: Broadcast['audience_filter'],
  svSubLabel: (code: string) => string,
): { text: string; mode: 'upload' | 'service' } {
  if (filter && typeof filter === 'object' && 'mode' in filter && filter.mode === 'upload') {
    return { text: 'Uploaded list', mode: 'upload' };
  }
  const f = (filter ?? {}) as AudienceFilter;
  const services = f.service_types ?? [];
  const labels = services
    .map((v) => SERVICE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v)
    .join(', ');
  let scope = '';
  if (services.includes('sim_verifier') && f.sv_scope) {
    scope =
      f.sv_scope === 'all' ? 'All' : f.sv_scope === 'none' ? 'No sub-type' : svSubLabel(f.sv_scope);
  }
  const text = labels ? `${labels}${scope ? ` · ${scope}` : ''}` : 'All users';
  return { text, mode: 'service' };
}

/** Right-aligned entry time: clock for today, else short month/day. */
function entryTime(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Date-divider label: Today / Yesterday / weekday (this week) / month-day. */
function dayGroupLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startEntry = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startToday - startEntry) / 86_400_000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString(
    [],
    d.getFullYear() === now.getFullYear()
      ? { month: 'long', day: 'numeric' }
      : { month: 'long', day: 'numeric', year: 'numeric' },
  );
}

/** Timeline-node color classes (bg + soft ring) by delivery outcome. */
function historyNodeClass(bc: Broadcast): string {
  if (bc.status === 'sending') return 'bg-primary-600 ring-primary-600/25';
  if (bc.status === 'completed')
    return bc.failed_count > 0 ? 'bg-amber-500 ring-amber-500/25' : 'bg-green-500 ring-green-500/25';
  if (bc.status === 'queued') return 'bg-yellow-400 ring-yellow-400/25';
  return 'bg-gray-300 ring-gray-300/40'; // draft / cancelled
}

// ── Progress bar helper ───────────────────────────────────────────────────────
function ProgressBar({ value, total, color }: { value: number; total: number; color: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2">
      <div className={`h-2 rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// ── Multi-select toggle helper ────────────────────────────────────────────────
function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (val: string) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val]);
  };
  return (
    <div>
      <span className="block text-xs font-medium text-text-secondary mb-1">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map(({ value, label: optLabel }) => (
          <button
            key={value}
            type="button"
            onClick={() => toggle(value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              selected.includes(value)
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-text-secondary border-border-primary hover:bg-gray-50'
            }`}
          >
            {optLabel}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── AnnouncementsScreen ───────────────────────────────────────────────────────

interface AnnouncementsScreenProps {
  onBack: () => void;
  /** Phase 39 (D-02): pre-fills the send-test chat-id field with the
   *  logged-in technician's own Telegram chat id when linked. Editable. */
  defaultTestChatId?: string;
}

type ScreenView = 'list' | 'detail';

export function AnnouncementsScreen({ onBack, defaultTestChatId }: AnnouncementsScreenProps) {
  // Get auth token for socket subscription (same pattern as Dashboard.tsx)
  const authToken = localStorage.getItem('support_auth_token') || '';
  const {
    broadcasts,
    isLoading,
    error,
    loadBroadcasts,
    refreshBroadcasts,
    previewAudience,
    createBroadcast,
    cancelBroadcast,
    retryFailed,
    sendTest,
    getBroadcast,
    loadSimVerifierSubs,
    uploadRecipients,
    createFromUpload,
    getInvalidLink,
  } = useAnnouncements();

  // SV sub-category friendly-label map (D-01 — raw uppercased fallback).
  const { labelFor: svSubLabel } = useSimVerifierSubLabels();

  // Socket subscription for live broadcast_progress on 'stats' room
  const { lastMessage } = useTicketWebSocket({
    ticketId: 'stats',
    authToken,
    enabled: true,
  });

  // ── Screen state ──────────────────────────────────────────────────────────
  const [view, setView] = useState<ScreenView>('list');
  const [selectedBroadcast, setSelectedBroadcast] = useState<BroadcastDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ── Compose form state ────────────────────────────────────────────────────
  const [showCompose, setShowCompose] = useState(false);
  // ── Phase 41: full-screen wizard step machine ─────────────────────────────
  // 1 = Compose (title/message/link + live preview), 2 = Audience, 3 = Review.
  // The rail derives done/active/pending from this; the footer ✕/Back/Next
  // navigate it. Reset to 1 on open and on close so a re-open starts on Compose.
  const [composeStep, setComposeStep] = useState<1 | 2 | 3>(1);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  // Optional link — sent to Telegram with a large preview (thumbnail) above
  // the text; the title renders bold. Empty = plain text-only announcement.
  const [composeLink, setComposeLink] = useState('');
  // Optional tappable label for the link (rendered bracketed). Empty → "Video Link".
  const [composeLinkLabel, setComposeLinkLabel] = useState('');
  // Audience filter state — service type only (Task 3: categories/date/status removed)
  const [filterServiceTypes, setFilterServiceTypes] = useState<string[]>([]);
  // ── Phase 40: audience mode (mutually exclusive — SPEC-R8) ────────────────
  // 'service' = the service-type + SV-sub builder; 'upload' = a CSV recipient
  // list. Switching to 'service' discards any selected file + dry-run summary.
  const [audienceMode, setAudienceMode] = useState<'service' | 'upload'>('service');
  // ── Phase 40: SIM Verifier sub-scope (D-01) ───────────────────────────────
  // 'none' preserves the Phase 39 canonical-only behaviour; 'all' = every SV
  // ref; '<code>' = a single discovered sub-category. Only meaningful when
  // 'sim_verifier' is among the selected service types.
  const [svScope, setSvScope] = useState<'all' | 'none' | string>('none');
  // Discovered SV sub-category codes (data-driven — GET /sim-verifier-subs).
  const [svSubs, setSvSubs] = useState<string[]>([]);
  // ── Phase 40: CSV upload state (upload mode) ──────────────────────────────
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadSummary, setUploadSummary] = useState<UploadSummary | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  // Live estimate
  const [estimatedCount, setEstimatedCount] = useState<number | null>(null);
  const [estimateLoading, setEstimateLoading] = useState(false);
  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Test-send state
  const [testChatId, setTestChatId] = useState(defaultTestChatId ?? '');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  // CSV action state — tracks WHICH action is in-flight so only the clicked
  // button spins (Save must not show its spinner while Preview/Copy link run).
  const [csvBusy, setCsvBusy] = useState<null | 'save' | 'invalid' | 'preview' | 'copy'>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  // Filename of a just-saved CSV → shows a success hint with where to find it.
  const [csvSaved, setCsvSaved] = useState<string | null>(null);
  // Signed CSV link (shown so it can be copied as a URL / opened on another device)
  const [csvLink, setCsvLink] = useState<string | null>(null);
  const [csvLinkCopied, setCsvLinkCopied] = useState(false);
  // In-app CSV preview (rendered on-page as a table, no external browser)

  // ── Confirm state (inline typed-count, Step 3) ────────────────────────────
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // The number the admin must re-type to unlock Send — the inline replacement
  // for the removed typed-count confirm modal. Reset on entering Step 3 / re-open.
  const [confirmTyped, setConfirmTyped] = useState('');

  // ── Load list on mount and whenever we return to the list view ───────────
  // Returning from the detail (where a broadcast may have just completed) must
  // refresh the list so its status chip / "N/N sent" isn't a stale snapshot.
  useEffect(() => {
    if (view === 'list') void loadBroadcasts();
  }, [view, loadBroadcasts]);

  // ── Poll the list while any broadcast is still 'sending' ──────────────────
  // Same stale-snapshot problem as the detail view: the list is a point-in-time
  // fetch, so an in-flight broadcast would show "Sending 0/N" forever even after
  // it completes. Poll every 3s while anything is sending; stop once none are
  // (hasSending flips false → effect re-runs → clears the timer).
  //
  // Use refreshBroadcasts (not loadBroadcasts) so the poll updates the data
  // silently — no isLoading toggle, so the list stays mounted and only the
  // progress bar / sent count / status chip re-render. loadBroadcasts is kept
  // for the initial mount load, where showing the spinner once is correct.
  const hasSending = broadcasts.some((b) => b.status === 'sending');
  useEffect(() => {
    if (view !== 'list' || !hasSending) return;
    const timer = setInterval(() => {
      void refreshBroadcasts();
    }, 3000);
    return () => clearInterval(timer);
  }, [view, hasSending, refreshBroadcasts]);

  // ── Build current audience filter from form state ─────────────────────────
  // Service type only — categories/date range/ticket_status removed from UI
  // (Task 3). The AudienceFilter type still accepts those fields so the backend
  // contract is unchanged; we simply never populate them from the UI.
  const buildFilter = useCallback((): AudienceFilter => {
    const filter: AudienceFilter = {};
    if (filterServiceTypes.length > 0) filter.service_types = filterServiceTypes;
    // Phase 40 (D-01): only forward sv_scope when SIM Verifier is targeted —
    // it's meaningless (and ignored server-side) otherwise. Forwarded verbatim;
    // the server preview does NOT strip it.
    if (filterServiceTypes.includes('sim_verifier')) filter.sv_scope = svScope;
    return filter;
  }, [filterServiceTypes, svScope]);

  // ── Debounced live estimate on filter change ──────────────────────────────
  useEffect(() => {
    if (!showCompose) return;
    // Upload mode has no filter-based estimate — the dry-run matched/invalid
    // summary is its equivalent, so skip the preview call entirely there.
    if (audienceMode === 'upload') return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(async () => {
      const filter = buildFilter();
      setEstimateLoading(true);
      const result = await previewAudience(filter);
      setEstimateLoading(false);
      if (result.ok) {
        setEstimatedCount(result.count);
      } else {
        setEstimatedCount(null);
      }
    }, 400);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [showCompose, audienceMode, buildFilter, previewAudience]);

  // ── Live progress: a socket event triggers an authoritative re-fetch ──────
  // A broadcast_progress event for the OPEN broadcast is treated as a SIGNAL to
  // re-pull GET /:id, NOT as a payload we apply directly: the event lacks the
  // invalid breakdown (so `total - sent - failed - blocked` mis-counts invalid
  // rows as pending) and its counts would be masked by the report-preferred
  // display (WR-03) anyway. Re-fetching keeps the report + counts consistent.
  // Throttled to ~1/sec so a busy send can't spam the endpoint.
  const lastProgressRefetchAt = useRef(0);
  useEffect(() => {
    if (!lastMessage || lastMessage.type !== 'broadcast_progress') return;
    const event = lastMessage.payload as BroadcastProgressEvent;
    const openId = selectedBroadcast?.id;
    if (!openId || event.broadcastId !== openId) return;
    const now = Date.now();
    if (now - lastProgressRefetchAt.current < 1000) return;
    lastProgressRefetchAt.current = now;
    void getBroadcast(openId).then((fresh) => { if (fresh) setSelectedBroadcast(fresh); });
  }, [lastMessage, selectedBroadcast?.id, getBroadcast]);

  // ── Poll the detail until it SETTLES ──────────────────────────────────────
  // Fixes the fast-send freeze: the one-shot completion socket event can fire
  // during the create→open window (before this broadcast is selected) and get
  // dropped, and a small send is already past 'sending' by the time the detail
  // opens — so a status==='sending' gated poll never runs and the view stays
  // frozen on a stale snapshot (e.g. "completed" with counts still showing
  // pending) until a manual Refresh.
  //
  // Instead we poll every 1.5s whenever the broadcast is NOT settled — still
  // sending/queued, OR any recipients still pending (which also catches the
  // non-atomic window where status flipped to completed before the counters
  // caught up). A few confirming polls after it looks settled let a lagging
  // snapshot converge; the poll then stops (bounded — a genuinely stuck
  // broadcast can't poll forever, and an already-settled old broadcast opened
  // from history never starts).
  useEffect(() => {
    if (view !== 'detail' || !selectedBroadcast) return;
    const unsettled = (bc: BroadcastDetail) =>
      bc.status === 'sending' || bc.status === 'queued' || (bc.pending_count ?? 0) > 0;
    if (!unsettled(selectedBroadcast)) return; // already settled → nothing to poll
    const id = selectedBroadcast.id;
    let confirmsLeft = 3; // extra polls after it looks settled, so counts converge
    let cancelled = false;
    const timer = setInterval(async () => {
      const fresh = await getBroadcast(id);
      if (cancelled || !fresh) return;
      setSelectedBroadcast(fresh);
      if (unsettled(fresh)) confirmsLeft = 3;
      else if (--confirmsLeft <= 0) clearInterval(timer);
    }, 1500);
    return () => { cancelled = true; clearInterval(timer); };
    // Intentionally keyed on the broadcast id only: the guard reads the snapshot
    // at open, and the interval self-manages via getBroadcast — re-running on
    // every count change would tear down/recreate the timer each tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedBroadcast?.id, getBroadcast]);

  // ── Reset the CSV-upload sub-state (used on mode-switch + compose open) ────
  const clearUploadState = useCallback(() => {
    setUploadFile(null);
    setUploadSummary(null);
    setUploadBusy(false);
    setUploadError(null);
    if (uploadInputRef.current) uploadInputRef.current.value = '';
  }, []);

  // ── Open compose sheet ────────────────────────────────────────────────────
  const openCompose = () => {
    setComposeStep(1);
    setComposeTitle('');
    setComposeMessage('');
    setComposeLink('');
    setComposeLinkLabel('');
    setFilterServiceTypes([]);
    setEstimatedCount(null);
    setTestChatId('');
    setTestResult(null);
    // Reset the inline typed-count confirm + its error (Step 3).
    setConfirmTyped('');
    setConfirmError(null);
    // Phase 40: reset audience mode + SV scope + upload state.
    setAudienceMode('service');
    setSvScope('none');
    clearUploadState();
    setShowCompose(true);
    // Discover SV sub-categories live (data-driven — never hardcoded). Errors
    // are non-fatal: the picker just shows All / No sub-type with no chips.
    void loadSimVerifierSubs().then(setSvSubs);
  };

  // ── Phase 41: close the wizard flow (✕ / Cancel on Step 1) ────────────────
  // Resets the step so a re-open starts on Compose, then hides the wizard.
  const closeCompose = () => {
    setComposeStep(1);
    setShowCompose(false);
  };

  // ── Phase 40: switch audience mode (service ⇆ upload) ──────────────────────
  // Switching back to 'service' DISCARDS the upload (SPEC-R8: the modes are
  // mutually exclusive — an upload never combines with a service-type filter).
  const switchAudienceMode = (mode: 'service' | 'upload') => {
    if (mode === audienceMode) return;
    if (mode === 'service') clearUploadState();
    setAudienceMode(mode);
  };

  // ── Phase 40: pick a CSV file → run the dry-run summary ────────────────────
  const handleUploadFile = async (file: File) => {
    setUploadFile(file);
    setUploadSummary(null);
    setUploadError(null);
    setUploadBusy(true);
    const result = await uploadRecipients(file);
    setUploadBusy(false);
    if (result.ok) {
      setUploadSummary(result.summary);
    } else {
      setUploadError(result.error);
    }
  };

  // ── Phase 40: download the recipient CSV template (D-05) ───────────────────
  const handleDownloadTemplate = async () => {
    setTemplateBusy(true);
    setUploadError(null);
    const result = await apiClient.downloadRecipientTemplate();
    setTemplateBusy(false);
    if (!result.ok) setUploadError(result.error);
  };

  // ── Open detail view ──────────────────────────────────────────────────────
  const openDetail = async (broadcast: Broadcast) => {
    setDetailLoading(true);
    setView('detail');
    const detail = await getBroadcast(broadcast.id);
    setSelectedBroadcast(detail);
    setDetailLoading(false);
  };

  // ── Test-send (D-02) ──────────────────────────────────────────────────────
  const handleTestSend = async () => {
    if (!testChatId.trim() || !composeMessage.trim()) {
      setTestResult({ ok: false, message: 'Enter a chat ID and message first' });
      return;
    }
    setTestSending(true);
    setTestResult(null);
    const result = await sendTest(testChatId.trim(), composeMessage, {
      title: composeTitle.trim(),
      linkUrl: composeLink.trim() || undefined,
      linkLabel: composeLinkLabel.trim() || undefined,
    });
    setTestSending(false);
    if (result.ok) {
      setTestResult({ ok: true, message: 'Test delivered ✓' });
    } else {
      setTestResult({ ok: false, message: result.error });
    }
  };

  // The count the inline typed-count confirm guards against — the matched upload
  // count in upload mode, else the filter estimate. Also the "Send to N" label.
  const confirmRecipientCount =
    audienceMode === 'upload' ? uploadSummary?.matched ?? 0 : estimatedCount ?? 0;

  // Typed-count confirm is a friction gate for LARGE blasts only. Below the
  // threshold the "type N to confirm" step is skipped and Step 3's Send is a
  // plain tap — a small, easily-reviewed send doesn't warrant the extra step.
  const requiresTypedConfirm = confirmRecipientCount >= TYPED_CONFIRM_THRESHOLD;

  // ── Confirm → createBroadcast (D-04) ─────────────────────────────────────
  const handleConfirmSend = async () => {
    setConfirmBusy(true);
    setConfirmError(null);

    let result: { ok: true; id: string } | { ok: false; error: string };
    if (audienceMode === 'upload') {
      // Upload mode (SPEC-R8): create from the uploaded CSV — the server
      // re-parses + re-resolves the same file, so no confirm_count is needed.
      if (!uploadFile) {
        setConfirmBusy(false);
        setConfirmError('No file selected.');
        return;
      }
      result = await createFromUpload(
        {
          title: composeTitle.trim(),
          message: composeMessage,
          link_url: composeLink.trim() || null,
          link_label: composeLinkLabel.trim() || null,
        },
        uploadFile,
      );
    } else {
      if (estimatedCount === null) {
        setConfirmBusy(false);
        return;
      }
      result = await createBroadcast({
        title: composeTitle.trim(),
        message: composeMessage,
        link_url: composeLink.trim() || null,
        link_label: composeLinkLabel.trim() || null,
        audience_filter: buildFilter(),
        confirm_count: estimatedCount,
      });
    }

    setConfirmBusy(false);
    if (!result.ok) {
      // 409 count-mismatch (service mode): surface the error, re-run preview.
      setConfirmError(result.error);
      if (audienceMode === 'service') {
        const freshResult = await previewAudience(buildFilter());
        if (freshResult.ok) setEstimatedCount(freshResult.count);
      }
      return;
    }
    // Success: close the wizard, navigate to detail (no modal to dismiss —
    // the typed-count confirm is inline in Step 3 now).
    setShowCompose(false);
    // Navigate to the new broadcast's detail
    if (result.id) {
      const detail = await getBroadcast(result.id);
      setSelectedBroadcast(detail);
      setView('detail');
    } else {
      setView('list');
    }
  };

  // ── Resume a stuck broadcast ──────────────────────────────────────────────
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const handleResume = async () => {
    if (!selectedBroadcast) return;
    setResumeBusy(true);
    setResumeError(null);
    const result = await apiClient.resumeBroadcast(selectedBroadcast.id);
    setResumeBusy(false);
    if (!result.success) {
      setResumeError(result.error ?? 'Failed to resume broadcast');
    } else {
      // Refresh detail to show updated pending count
      const detail = await getBroadcast(selectedBroadcast.id);
      setSelectedBroadcast(detail);
    }
  };

  // ── Cancel a broadcast ────────────────────────────────────────────────────
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const handleCancel = async () => {
    if (!selectedBroadcast) return;
    setCancelBusy(true);
    setCancelError(null);
    const result = await cancelBroadcast(selectedBroadcast.id);
    setCancelBusy(false);
    if (!result.ok) {
      setCancelError(result.error);
    } else {
      // Refresh detail
      const detail = await getBroadcast(selectedBroadcast.id);
      setSelectedBroadcast(detail);
    }
  };

  // ── Download CSV ──────────────────────────────────────────────────────────
  // Mint a signed public URL (no JWT needed for the download itself), then
  // either hand it to the native bridge (SuperApp) or open it in the browser
  // (Content-Disposition triggers the save). Both paths work across devices
  // because the URL is shareable for up to 1 hour.
  const handleDownloadCsv = async () => {
    if (!selectedBroadcast) return;
    const id = selectedBroadcast.id;
    setCsvSaved(null);
    setCsvBusy('save');
    setCsvError(null);
    try {
      if (bridgeService.isInSuperApp) {
        // Fetch the CSV text via the authenticated endpoint (same mechanism as
        // every other API call, which works reliably in the webview — the public
        // signed-URL fetch did not) and hand the bytes to the native saveFile so
        // the device writes the REAL CSV. TextEncoder → base64 preserves UTF-8.
        const result = await apiClient.getBroadcastCsvText(id);
        if (!result.ok || result.text == null) {
          setCsvError('Could not download the CSV file. Try Copy link instead.');
          return;
        }
        // Save the real bytes directly to the device (the native share-sheet
        // picker is unreliable here — iOS sharePositionOrigin bug). Timeout-
        // guarded so a non-responsive native bridge can't hang the spinner.
        const bytes = new TextEncoder().encode(result.text);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const ok = await withTimeout(
          bridgeService.saveFile(btoa(binary), `broadcast-${id}.csv`, 'csv', 'anyFile'),
        );
        if (ok) setCsvSaved(`broadcast-${id}.csv`);
        else setCsvError('Could not save the CSV file. Try Copy link instead.');
      } else {
        // Browser: Content-Disposition: attachment on the signed URL triggers the download.
        const linkResult = await apiClient.getBroadcastCsvLink(id);
        if (!linkResult.success || !linkResult.data) {
          setCsvError(linkResult.error ?? 'Failed to generate download link');
          return;
        }
        window.open(linkResult.data.url, '_blank');
      }
    } finally {
      setCsvBusy(null);
    }
  };

  // ── Copy the CSV as a shareable LINK (URL text, not the file) ─────────────
  // The share-sheet "Copy" copies the downloaded FILE (you get a file path).
  // This puts the actual signed URL on the clipboard so it can be pasted /
  // opened on another device. The URL is also rendered selectable as a fallback.
  const handleCopyCsvLink = async () => {
    if (!selectedBroadcast) return;
    setCsvBusy('copy');
    setCsvError(null);
    setCsvLinkCopied(false);
    const linkResult = await apiClient.getBroadcastCsvLink(selectedBroadcast.id);
    setCsvBusy(null);
    if (!linkResult.success || !linkResult.data) {
      setCsvError(linkResult.error ?? 'Failed to generate download link');
      return;
    }
    const { url } = linkResult.data;
    setCsvLink(url);
    setCsvLinkCopied(await copyToClipboard(url));
  };

  // ── Preview the CSV as a server-rendered HTML table ───────────────────────
  // Same UX as the chat media CSV preview: mint the signed link and open the
  // `?format=html` variant in the SuperApp in-app browser. The server wraps
  // the CSV in an HTML <table> so iOS/WebViews render it inline (they refuse
  // to render a `.csv` URL no matter its Content-Type).
  const handlePreviewCsv = async () => {
    if (!selectedBroadcast) return;
    setCsvBusy('preview');
    setCsvError(null);
    const linkResult = await apiClient.getBroadcastCsvLink(selectedBroadcast.id);
    setCsvBusy(null);
    if (!linkResult.success || !linkResult.data) {
      setCsvError(linkResult.error ?? 'Failed to generate preview link');
      return;
    }
    const { url } = linkResult.data;
    const target = `${url}${url.includes('?') ? '&' : '?'}format=html`;
    if (bridgeService.isInSuperApp) {
      bridgeService
        .openExternalLink(target, 'inAppBrowserView')
        .catch(() => window.open(target, '_blank'));
    } else {
      window.open(target, '_blank');
    }
  };

  // ── Retry failed recipients ───────────────────────────────────────────────
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const handleRetry = async () => {
    if (!selectedBroadcast) return;
    setRetryBusy(true);
    setRetryError(null);
    const result = await retryFailed(selectedBroadcast.id);
    setRetryBusy(false);
    if (!result.ok) {
      setRetryError(result.error);
    } else {
      const detail = await getBroadcast(selectedBroadcast.id);
      setSelectedBroadcast(detail);
    }
  };

  // ── Phase 40: manual refresh of the detail (report card, D-04) ────────────
  // Independent of the socket/polling — re-fetches GET /:id so the counts +
  // report.last_updated re-sync on demand.
  const [reportRefreshing, setReportRefreshing] = useState(false);
  const handleRefreshReport = async () => {
    if (!selectedBroadcast) return;
    setReportRefreshing(true);
    const detail = await getBroadcast(selectedBroadcast.id);
    setReportRefreshing(false);
    if (detail) setSelectedBroadcast(detail);
  };

  // ── Phase 40: download the invalid-recipients CSV (D-03) ──────────────────
  // Reuses the same csvBusy/csvSaved/csvError machinery as the recipient export
  // for a consistent SuperApp-save / browser-download experience.
  const handleDownloadInvalid = async () => {
    if (!selectedBroadcast) return;
    const id = selectedBroadcast.id;
    setCsvSaved(null);
    setCsvBusy('invalid'); // distinct from the recipient 'save' so only THIS button spins
    setCsvError(null);
    try {
      if (bridgeService.isInSuperApp) {
        // Fetch via the AUTHENTICATED /invalid.csv endpoint (reliable in the
        // webview) and hand the real bytes to native saveFile — mirrors the
        // recipient export. The old raw fetch() of the public signed URL was
        // unreliable in the webview and was the cause of the download failure.
        const result = await apiClient.getInvalidCsvText(id);
        if (!result.ok || result.text == null) {
          setCsvError('Could not download the invalid-recipients CSV. Try Copy link instead.');
          return;
        }
        const bytes = new TextEncoder().encode(result.text);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const ok = await withTimeout(
          bridgeService.saveFile(btoa(binary), `broadcast-${id}-invalid.csv`, 'csv', 'anyFile'),
        );
        if (ok) setCsvSaved(`broadcast-${id}-invalid.csv`);
        else setCsvError('Could not save the invalid-recipients CSV.');
      } else {
        // Browser: the public signed link's Content-Disposition triggers the save.
        const url = await getInvalidLink(id);
        if (!url) {
          setCsvError('Failed to generate the invalid-recipients download link.');
          return;
        }
        window.open(url, '_blank');
      }
    } finally {
      setCsvBusy(null);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // List view
  const renderList = () => (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Sticky header — Back + centered title only. The New action lives in the
          content below (Phase 42), not in the nav chrome.
          text-[#007AFF] is iOS system blue, used INTENTIONALLY on the list/detail
          Back affordance as the platform convention for navigation chrome. It is
          deliberately distinct from the compose wizard's brand accent (primary
          token, #2563eb), reserved for the high-stakes CTA/active surface — a
          platform-vs-brand split, not an inconsistency (41-UI-REVIEW.md #1). */}
      <div className="sticky top-0 z-10 bg-white border-b border-border-primary px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-0.5 text-[#007AFF] -ml-1 touch-active"
            aria-label="Back"
          >
            <ChevronLeft className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} aria-hidden />
            <span className="text-[17px] leading-none">Back</span>
          </button>
          <h1 className="flex-1 text-base font-semibold text-text-primary text-center flex items-center justify-center gap-1.5">
            <Megaphone className="h-4 w-4" aria-hidden />
            Announcements
          </h1>
          <span className="w-[52px]" aria-hidden />
        </div>
      </div>

      {/* Inline error banner */}
      {error && (
        <div className="mx-4 sm:mx-6 mt-3 rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-text-secondary" />
          </div>
        ) : broadcasts.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="h-10 w-10 text-gray-300 mx-auto mb-3" aria-hidden />
            <p className="text-sm text-text-secondary">No announcements yet.</p>
            <button
              type="button"
              onClick={openCompose}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
            >
              <Plus className="h-4 w-4" aria-hidden />
              New announcement
            </button>
          </div>
        ) : (
          <>
            {/* Primary create action — moved into the content flow (Phase 42). */}
            <button
              type="button"
              onClick={openCompose}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3.5 text-[15px] font-semibold text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm"
            >
              <Plus className="h-5 w-5" aria-hidden />
              New announcement
            </button>

            {/* History section header */}
            <div className="flex items-baseline justify-between mt-6 mb-1 px-0.5">
              <h2 className="text-xs font-bold uppercase tracking-wider text-text-tertiary">
                History
              </h2>
              <span className="text-xs font-medium text-text-tertiary">
                {broadcasts.length} {broadcasts.length === 1 ? 'announcement' : 'announcements'}
              </span>
            </div>

            {/* Date-grouped history timeline. broadcasts arrive sorted created_at
                DESC; we insert a divider when the day-group changes and draw the
                timeline stem only within a group. */}
            <div>
              {broadcasts.map((bc, idx) => {
                const group = dayGroupLabel(bc.created_at);
                const prevGroup =
                  idx > 0 ? dayGroupLabel(broadcasts[idx - 1]!.created_at) : null;
                const nextGroup =
                  idx < broadcasts.length - 1
                    ? dayGroupLabel(broadcasts[idx + 1]!.created_at)
                    : null;
                const showDivider = group !== prevGroup;
                const connectStem = nextGroup === group;
                const chip = statusChip(bc.status);
                const aud = describeAudience(bc.audience_filter, svSubLabel);
                const total = bc.total_recipients || 0;
                return (
                  <Fragment key={bc.id}>
                    {showDivider && (
                      <div className="flex items-center gap-2 mt-4 mb-2 px-0.5">
                        <span className="text-xs font-semibold text-text-secondary">{group}</span>
                        <span className="flex-1 h-px bg-border-primary" />
                      </div>
                    )}
                    <div className="flex gap-3">
                      {/* Timeline spine: status node + connecting stem. */}
                      <div className="flex flex-col items-center pt-3 flex-shrink-0">
                        <span
                          className={`h-2.5 w-2.5 rounded-full ring-2 ${historyNodeClass(bc)}`}
                          aria-hidden
                        />
                        {connectStem && <span className="flex-1 w-0.5 bg-border-primary mt-1" />}
                      </div>
                      {/* History entry card */}
                      <button
                        type="button"
                        onClick={() => void openDetail(bc)}
                        className="flex-1 min-w-0 text-left bg-white rounded-xl border border-border-primary px-3.5 py-3 mb-2.5 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-text-primary leading-snug flex-1 min-w-0 truncate">
                            {bc.title}
                          </p>
                          <span
                            className={`flex-shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${chip.className}`}
                          >
                            {chip.label}
                          </span>
                        </div>
                        {bc.message && (
                          <p className="text-xs text-text-secondary mt-1 truncate">{bc.message}</p>
                        )}
                        {/* Delivery mini-bar — every delivery-report category in its
                            report colour, so the bar always sums to the total:
                            sent (green) · unsent (yellow) · error (red) ·
                            blocked (gray) · invalid (orange). */}
                        {total > 0 && (() => {
                          // Defensive ?? 0 (the type contract is weakest here), and
                          // width % is taken against a denominator that can't be
                          // smaller than the sum of the parts — so if the pinned
                          // counters drift above total (e.g. after a retry-failed
                          // cycle) the segments still can't overflow 100%.
                          const sent = bc.sent_count ?? 0;
                          const failed = bc.failed_count ?? 0;
                          const blocked = bc.blocked_count ?? 0;
                          const invalid = bc.invalid_count ?? 0;
                          const pendingN = Math.max(0, total - sent - failed - blocked - invalid);
                          const denom = Math.max(total, sent + failed + blocked + invalid + pendingN) || 1;
                          const pct = (n: number) => (n / denom) * 100;
                          return (
                            <div className="flex h-1 rounded-full overflow-hidden bg-gray-100 mt-2.5">
                              {sent > 0 && (
                                <div className="bg-green-500" style={{ width: `${pct(sent)}%` }} />
                              )}
                              {pendingN > 0 && (
                                <div className="bg-yellow-400" style={{ width: `${pct(pendingN)}%` }} />
                              )}
                              {failed > 0 && (
                                <div className="bg-red-400" style={{ width: `${pct(failed)}%` }} />
                              )}
                              {blocked > 0 && (
                                <div className="bg-gray-400" style={{ width: `${pct(blocked)}%` }} />
                              )}
                              {invalid > 0 && (
                                <div className="bg-orange-400" style={{ width: `${pct(invalid)}%` }} />
                              )}
                            </div>
                          );
                        })()}
                        {/* Meta: audience · sent count · time. */}
                        <div className="flex items-center gap-2 mt-2 text-[11px] text-text-tertiary">
                          <span className="inline-flex items-center gap-1 font-medium text-text-secondary min-w-0">
                            {aud.mode === 'upload' ? (
                              <Upload className="h-3 w-3 flex-shrink-0" aria-hidden />
                            ) : (
                              <Users className="h-3 w-3 flex-shrink-0" aria-hidden />
                            )}
                            <span className="truncate">{aud.text}</span>
                          </span>
                          <span
                            className="h-0.5 w-0.5 rounded-full bg-gray-300 flex-shrink-0"
                            aria-hidden
                          />
                          <span className="whitespace-nowrap">
                            {bc.sent_count} / {total} sent
                          </span>
                          <span className="ml-auto whitespace-nowrap flex-shrink-0">
                            {entryTime(bc.created_at)}
                          </span>
                        </div>
                      </button>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Detail view
  const renderDetail = () => {
    const bc = selectedBroadcast;
    const total = bc?.total_recipients ?? 0;
    // WR-03: source the progress bars from the LIVE report when present so they
    // agree with the Delivery-report card below. The pinned counter columns
    // (sent_count/failed_count/…) drift from the live GROUP BY after a
    // retry-failed cycle; the report is the single source of truth. Fall back to
    // the pinned counters only when no report is attached (same fallback the
    // report card uses).
    const report = bc?.report;
    const sentCount = report?.sent ?? bc?.sent_count ?? 0;
    const failedCount = report?.error ?? bc?.failed_count ?? 0;
    const blockedCount = report?.blocked ?? bc?.blocked_count ?? 0;
    const pending = report?.unsent ?? bc?.pending_count ?? 0;
    const chip = bc ? statusChip(bc.status) : null;

    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Fixed header — stays put while the content below scrolls */}
        <div className="sticky top-0 z-10 bg-white border-b border-border-primary px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setView('list'); setSelectedBroadcast(null); }}
              className="flex items-center gap-0.5 text-[#007AFF] -ml-1 touch-active"
              aria-label="Back to list"
            >
              <ChevronLeft className="h-6 w-6 flex-shrink-0" strokeWidth={2.5} aria-hidden />
              <span className="text-[17px] leading-none">Back</span>
            </button>
            <h1 className="flex-1 text-base font-semibold text-text-primary text-center">
              Broadcast Detail
            </h1>
            <span className="w-[50px]" aria-hidden />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
          {detailLoading || !bc ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-text-secondary" />
            </div>
          ) : (
            <>
              {/* Announcement card — the plain-text message is the focus */}
              <div className="rounded-xl border border-border-primary bg-white overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-primary">
                  <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-text-primary leading-tight truncate">
                      {bc.title}
                    </h2>
                    <p className="text-xs text-text-secondary mt-0.5">
                      {total.toLocaleString()} recipient{total === 1 ? '' : 's'}
                    </p>
                  </div>
                  {chip && (
                    <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${chip.className}`}>
                      {chip.label}
                    </span>
                  )}
                </div>
                {/* The message itself — the main content, comfortably spaced */}
                <div className="px-4 py-3.5">
                  {/* Message + the link's bracketed [label] appended INLINE at the
                      end of the body — mirrors exactly how the sent Telegram
                      message renders it (bold [Video Link] hyperlink, no raw URL).
                      Tapping the label opens the link. */}
                  <p className="text-[15px] leading-relaxed text-text-primary whitespace-pre-wrap break-words">
                    {bc.message}
                    {bc.link_url && (
                      <>
                        {' '}
                        <a
                          href={bc.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-primary-600"
                        >
                          [{bc.link_label?.trim() || 'Video Link'}]
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Progress */}
              <div className="bg-gray-50 rounded-xl border border-border-primary p-4 space-y-3">
                <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Delivery progress</p>
                {bc.status === 'completed' && (
                  <p className="text-sm font-semibold text-green-700">
                    Broadcast completed
                  </p>
                )}
                <div className="space-y-2">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-green-700 font-medium">Sent</span>
                      <span className="text-text-secondary">{sentCount} / {total}</span>
                    </div>
                    <ProgressBar value={sentCount} total={total} color="bg-green-500" />
                  </div>
                  {failedCount > 0 && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-red-700 font-medium">Failed</span>
                        <span className="text-text-secondary">{failedCount} / {total}</span>
                      </div>
                      <ProgressBar value={failedCount} total={total} color="bg-red-400" />
                    </div>
                  )}
                  {blockedCount > 0 && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600 font-medium">Blocked</span>
                        <span className="text-text-secondary">{blockedCount} / {total}</span>
                      </div>
                      <ProgressBar value={blockedCount} total={total} color="bg-gray-400" />
                    </div>
                  )}
                  {pending > 0 && (
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-yellow-700 font-medium">Pending</span>
                        <span className="text-text-secondary">{pending} / {total}</span>
                      </div>
                      <ProgressBar value={pending} total={total} color="bg-yellow-400" />
                    </div>
                  )}
                </div>
              </div>

              {/* ── Delivery report (D-04) — five DGSUPPORT-style labelled rows.
                     Prefers bc.report; falls back to mapping the existing counts
                     when report is absent (invalid ← report.invalid ?? invalid_count). */}
              {(() => {
                // Reuse the same report-preferred counts the progress bars use
                // (WR-03) so the two panels can never disagree.
                const rows: { label: string; value: number; className: string }[] = [
                  { label: 'Sent', value: sentCount, className: 'text-green-700' },
                  { label: 'Unsent', value: pending, className: 'text-yellow-700' },
                  { label: 'Error', value: failedCount, className: 'text-red-700' },
                  { label: 'Blocked', value: blockedCount, className: 'text-gray-600' },
                  { label: 'Invalid', value: report?.invalid ?? bc.invalid_count, className: 'text-orange-700' },
                ];
                const invalidCount = report?.invalid ?? bc.invalid_count;
                return (
                  <div className="rounded-xl border border-border-primary bg-white p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                        Delivery report
                      </p>
                      <button
                        type="button"
                        onClick={() => void handleRefreshReport()}
                        disabled={reportRefreshing}
                        className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-60"
                        aria-label="Refresh delivery report"
                      >
                        <RefreshCw
                          className={`h-3.5 w-3.5 ${reportRefreshing ? 'animate-spin' : ''}`}
                          aria-hidden
                        />
                        Refresh
                      </button>
                    </div>

                    {/* Labelled rows (NOT a table — D-04) */}
                    <div className="space-y-1.5">
                      {rows.map((row) => (
                        <div key={row.label} className="flex items-baseline justify-between gap-2 text-sm">
                          <span className={`font-medium ${row.className}`}>{row.label}</span>
                          <span className="flex-1 mx-2 border-b border-dotted border-gray-200" aria-hidden />
                          <span className="font-semibold text-text-primary tabular-nums">
                            {row.value.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>

                    {report?.last_updated && (
                      <p className="text-[11px] text-text-secondary">
                        Last updated: {new Date(report.last_updated).toLocaleString()}
                      </p>
                    )}

                    {invalidCount > 0 && (
                      <button
                        type="button"
                        onClick={() => void handleDownloadInvalid()}
                        disabled={csvBusy !== null}
                        className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-60"
                      >
                        {csvBusy === 'invalid' ? (
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        ) : (
                          <Download className="h-4 w-4" aria-hidden />
                        )}
                        Download invalid recipients
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* Errors */}
              {cancelError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {cancelError}
                </div>
              )}
              {resumeError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {resumeError}
                </div>
              )}
              {retryError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {retryError}
                </div>
              )}
              {csvError && (
                <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {csvError}
                </div>
              )}
              {csvSaved && (
                <div className="rounded-xl bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700">
                  Saved <span className="font-medium">{csvSaved}</span> — find it in your <span className="font-medium">Files</span> / <span className="font-medium">Downloads</span> app.
                </div>
              )}

              {/* Send-status actions (Resume / Cancel / Retry) — only shown
                  while sending or when there are failures to act on. */}
              {(bc.status === 'sending' || bc.failed_count > 0) && (
                <div className="flex gap-3 flex-wrap">
                  {/* Resume only when a send has stalled with failures (D: user
                      wants Resume only when the send failed and there are remaining
                      recipients to continue) — not during a healthy in-progress send. */}
                  {bc.status === 'sending' && bc.failed_count > 0 && (
                    <button
                      type="button"
                      onClick={() => void handleResume()}
                      disabled={resumeBusy}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                    >
                      {resumeBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <PlayCircle className="h-4 w-4" aria-hidden />
                      )}
                      Resume
                    </button>
                  )}
                  {bc.status === 'sending' && (
                    <button
                      type="button"
                      onClick={() => void handleCancel()}
                      disabled={cancelBusy}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                    >
                      {cancelBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <Ban className="h-4 w-4" aria-hidden />
                      )}
                      Cancel broadcast
                    </button>
                  )}
                  {bc.failed_count > 0 && bc.status !== 'sending' && (
                    <button
                      type="button"
                      onClick={() => void handleRetry()}
                      disabled={retryBusy}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                    >
                      {retryBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : (
                        <RefreshCw className="h-4 w-4" aria-hidden />
                      )}
                      Retry failed
                    </button>
                  )}
                </div>
              )}

              {/* Recipient list — CSV export actions grouped under one card */}
              <div className="rounded-xl border border-border-primary bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                    Recipient list
                  </p>
                  <span className="text-xs font-medium text-text-secondary">
                    {total.toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => void handleDownloadCsv()}
                    disabled={csvBusy !== null}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-60 whitespace-nowrap"
                  >
                    {csvBusy === 'save' ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    ) : (
                      <Download className="h-4 w-4" aria-hidden />
                    )}
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => void handlePreviewCsv()}
                    disabled={csvBusy !== null}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-60 whitespace-nowrap"
                  >
                    {csvBusy === 'preview' && (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    )}
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleCopyCsvLink()}
                    disabled={csvBusy !== null}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium text-text-primary hover:bg-gray-50 disabled:opacity-60 whitespace-nowrap"
                  >
                    {csvBusy === 'copy' && (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    )}
                    Copy link
                  </button>
                </div>
              </div>
              {csvLink && (
                <div className="mt-2 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2">
                  <p className="text-xs text-text-secondary mb-1.5">
                    {csvLinkCopied
                      ? 'Link copied ✓ — paste into Safari to open (valid ~1 hour).'
                      : 'Tap the link to select it, then Copy — or Open it directly:'}
                  </p>
                  <input
                    autoFocus
                    readOnly
                    value={csvLink}
                    ref={(el) => el?.setSelectionRange(0, csvLink.length)}
                    onFocus={(e) => e.currentTarget.select()}
                    onClick={(e) => e.currentTarget.select()}
                    className="w-full bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-blue-700 select-all mb-2"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (bridgeService.isInSuperApp) void bridgeService.openExternalLink(csvLink);
                      else window.open(csvLink, '_blank');
                    }}
                    className="w-full rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100"
                  >
                    Open in browser
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Compose sheet content ─────────────────────────────────────────────────
  // Audience builder is service type only (Task 3). Categories, date range,
  // and ticket status have been removed from the form — the backend contract
  // is unchanged (AudienceFilter still accepts those fields; we just don't
  // send them, which targets all users of the chosen service type regardless
  // of ticket status or creation date).

  // ── Step 1 (Compose) fields — Title / Message / Link / Link-label ─────────
  // Reused verbatim from the former bottom-sheet composeContent; the live preview
  // card (Task 2) is appended below these fields in the wizard body.
  const step1Fields = (
    <>
      {/* Title — rendered as a bold heading in the Telegram message */}
      <label className="block">
        <span className="block text-xs font-medium text-text-secondary mb-1">Title</span>
        <input
          type="text"
          value={composeTitle}
          onChange={(e) => setComposeTitle(e.target.value)}
          placeholder="Announcement title"
          className={inputClass}
        />
        <span className="block text-[11px] text-text-secondary mt-1">Shown in bold at the top of the message.</span>
      </label>

      {/* Message */}
      <label className="block">
        <span className="block text-xs font-medium text-text-secondary mb-1">Message</span>
        <textarea
          value={composeMessage}
          onChange={(e) => setComposeMessage(e.target.value)}
          placeholder="Write your message here..."
          rows={5}
          className={`${inputClass} resize-none`}
        />
      </label>

      {/* Link (optional) — sent with a large preview/thumbnail above the text */}
      <label className="block">
        <span className="block text-xs font-medium text-text-secondary mb-1">Link (optional)</span>
        <input
          type="url"
          inputMode="url"
          value={composeLink}
          onChange={(e) => setComposeLink(e.target.value)}
          placeholder="https://… (e.g. a video link)"
          className={inputClass}
        />
        <span className="block text-[11px] text-text-secondary mt-1">
          A video or page link shows as a thumbnail preview in the message.
        </span>
      </label>

      {/* Link label (optional) — the tappable text, rendered bracketed. */}
      {composeLink.trim() && (
        <label className="block">
          <span className="block text-xs font-medium text-text-secondary mb-1">Link label (optional)</span>
          <input
            type="text"
            value={composeLinkLabel}
            onChange={(e) => setComposeLinkLabel(e.target.value)}
            placeholder="Video Link"
            className={inputClass}
          />
          <span className="block text-[11px] text-text-secondary mt-1">
            Shown as the tappable link, e.g. [{composeLinkLabel.trim() || 'Video Link'}].
          </span>
        </label>
      )}
    </>
  );

  // ── Step 2 (Audience) — segmented service/upload builder + prominent estimate
  // Relocated from the former single-scroll composeContent (Plan 02, R4). Reuses
  // the SAME state + handlers (switchAudienceMode, MultiSelect, svScope/svSubs,
  // the debounced preview effect, handleUploadFile/handleDownloadTemplate) — this
  // block only re-arranges the visuals into the sketch's segmented control (.seg),
  // data-driven SV sub-chips, and the prominent "Estimated recipients" stat card.
  const step2Audience = (
    <>
      {/* Segmented Service type / Upload CSV control (sketch .seg) — a filled
          pill track with the active option raised. Mutual exclusivity is enforced
          by switchAudienceMode (service discards any pending upload). */}
      <div className="flex gap-1 bg-surface-tertiary rounded-xl p-1">
        <button
          type="button"
          onClick={() => switchAudienceMode('service')}
          className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
            audienceMode === 'service'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'bg-transparent text-text-secondary'
          }`}
        >
          Service type
        </button>
        <button
          type="button"
          onClick={() => switchAudienceMode('upload')}
          className={`flex-1 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
            audienceMode === 'upload'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'bg-transparent text-text-secondary'
          }`}
        >
          Upload CSV
        </button>
      </div>

      {/* ── Service-type builder (mode: service) ────────────────────────── */}
      {audienceMode === 'service' && (
        <>
          {/* Service type multi-select — fixed enum (MULTI-select, Info 5) */}
          <MultiSelect
            label="Service type"
            options={SERVICE_TYPE_OPTIONS}
            selected={filterServiceTypes}
            onChange={setFilterServiceTypes}
          />

          {/* SIM Verifier sub-scope picker (R4, D-01) — only when SV is chosen.
              Discovered codes are data-driven; labels use the raw-uppercased
              fallback from useSimVerifierSubLabels (Info 4 — no hardcoded GF/PVR). */}
          {filterServiceTypes.includes('sim_verifier') && (
            <div>
              <span className="block text-xs font-medium text-text-secondary mb-1">
                SIM Verifier sub-type
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSvScope('all')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    svScope === 'all'
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-text-secondary border-border-primary hover:bg-gray-50'
                  }`}
                >
                  All SIM Verifier
                </button>
                <button
                  type="button"
                  onClick={() => setSvScope('none')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    svScope === 'none'
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-text-secondary border-border-primary hover:bg-gray-50'
                  }`}
                >
                  No sub-type
                </button>
                {svSubs.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setSvScope(code)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                      svScope === code
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-text-secondary border-border-primary hover:bg-gray-50'
                    }`}
                  >
                    {svSubLabel(code)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Prominent "Estimated recipients" stat (sketch .stat) — the primary-
              tinted card promotes the former inline estimate into the headline
              of the step. Reuses estimatedCount/estimateLoading; the debounced
              preview effect updates it on every scope change. */}
          <div className="flex items-center justify-between rounded-xl bg-primary-50 border border-primary-100 px-4 py-3.5">
            <span className="text-[13px] font-semibold text-text-secondary">
              Estimated recipients
            </span>
            {estimateLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary-700" aria-hidden />
            ) : (
              <span className="text-2xl font-extrabold text-primary-700 tabular-nums">
                {estimatedCount !== null ? estimatedCount.toLocaleString() : '—'}
              </span>
            )}
          </div>
        </>
      )}

      {/* ── CSV upload builder (mode: upload, R8 + D-05) ────────────────── */}
      {audienceMode === 'upload' && (
        <div className="space-y-3">
          {/* Dashed drop zone — the primary file-choose affordance (matches 41-SKETCH .drop) */}
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            disabled={uploadBusy}
            className="w-full rounded-2xl border-[1.5px] border-dashed border-border-secondary bg-surface-secondary px-4 py-6 text-center touch-active disabled:opacity-60"
          >
            {uploadBusy ? (
              <span className="flex items-center justify-center gap-2 text-sm text-text-secondary">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Checking recipients…
              </span>
            ) : uploadFile ? (
              <>
                <div className="text-sm font-medium text-text-primary truncate">{uploadFile.name}</div>
                <div className="text-xs text-text-tertiary mt-1">Tap to choose another file</div>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-text-secondary">Tap to choose a CSV file</div>
                <div className="text-xs text-text-tertiary mt-1">columns: phone_number, telegram_id</div>
              </>
            )}
          </button>
          <input
            ref={uploadInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleUploadFile(file);
            }}
          />

          {/* Download template — small link below the drop zone (matches 41-SKETCH .tmpl) */}
          <button
            type="button"
            onClick={() => void handleDownloadTemplate()}
            disabled={templateBusy}
            className="flex items-center gap-1.5 text-sm font-semibold text-primary-600 disabled:opacity-60"
          >
            {templateBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="h-4 w-4" aria-hidden />
            )}
            Download template
          </button>

          {/* Matched-vs-invalid summary — the sketch's .match-row two-stat cards.
              NON-NEGOTIABLE (Concern 3): the malformed>0 count and the
              matched===0 "nothing to send" warning are preserved below. */}
          {uploadSummary && !uploadBusy && (
            <div className="space-y-2">
              <div className="flex gap-2.5">
                <div className="flex-1 rounded-xl border border-border-primary px-3 py-2.5 text-center">
                  <div className="text-xl font-bold text-green-600 tabular-nums">
                    {uploadSummary.matched}
                  </div>
                  <div className="text-[11px] font-semibold text-text-tertiary mt-0.5">Matched</div>
                </div>
                <div className="flex-1 rounded-xl border border-border-primary px-3 py-2.5 text-center">
                  <div className="text-xl font-bold text-red-600 tabular-nums">
                    {uploadSummary.invalid}
                  </div>
                  <div className="text-[11px] font-semibold text-text-tertiary mt-0.5">Invalid</div>
                </div>
                {uploadSummary.malformed > 0 && (
                  <div className="flex-1 rounded-xl border border-border-primary px-3 py-2.5 text-center">
                    <div className="text-xl font-bold text-orange-600 tabular-nums">
                      {uploadSummary.malformed}
                    </div>
                    <div className="text-[11px] font-semibold text-text-tertiary mt-0.5">
                      Malformed
                    </div>
                  </div>
                )}
              </div>
              {uploadSummary.matched === 0 && (
                <p className="text-xs text-red-600">
                  No rows matched a reachable user — nothing to send.
                </p>
              )}
            </div>
          )}

          {uploadError && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {uploadError}
            </div>
          )}
        </div>
      )}
    </>
  );

  // ── Step 3 (Review & Send) — summary + relocated Send Test + inline confirm
  // Plan 02 (R5). The audience description is derived from the same state Step 2
  // resolved: service mode → "<service labels> · <scope label>", upload mode →
  // "Uploaded list · <matched> matched". Recipients = confirmRecipientCount (the
  // matched upload count or the estimate) so it always matches Step 2.
  const step3ServiceLabels = filterServiceTypes
    .map((v) => SERVICE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v)
    .join(', ');
  const step3ScopeLabel = !filterServiceTypes.includes('sim_verifier')
    ? ''
    : svScope === 'all'
      ? 'All'
      : svScope === 'none'
        ? 'No sub-type'
        : svSubLabel(svScope);
  const step3AudienceDesc =
    audienceMode === 'upload'
      ? `Uploaded list · ${uploadSummary?.matched ?? 0} matched`
      : `${step3ServiceLabels || 'All users'}${step3ScopeLabel ? ` · ${step3ScopeLabel}` : ''}`;
  // Send is unlocked when either the typed confirm isn't required (small send) or
  // the typed number matches the recipient count.
  const step3ConfirmSatisfied =
    !requiresTypedConfirm || confirmTyped.trim() === String(confirmRecipientCount);
  const step3Review = (
    <>
      <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary">
        Review before sending
      </p>

      {/* Summary card (sketch .rev) — audience, recipient count, title, link. */}
      <div className="rounded-xl border border-border-primary p-4">
        <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-surface-tertiary">
          <span className="text-text-secondary">Audience</span>
          <span className="font-bold text-right max-w-[60%]">{step3AudienceDesc}</span>
        </div>
        <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-surface-tertiary">
          <span className="text-text-secondary">Recipients</span>
          <span className="font-bold text-right tabular-nums">
            {confirmRecipientCount.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between gap-3 py-1.5 text-sm border-b border-surface-tertiary">
          <span className="text-text-secondary">Title</span>
          <span className="font-bold text-right max-w-[60%] break-words">
            {composeTitle.trim() || '—'}
          </span>
        </div>
        {composeLink.trim() && (
          <div className="flex justify-between gap-3 py-1.5 text-sm">
            <span className="text-text-secondary">Link</span>
            <span className="font-bold text-right max-w-[60%] break-all text-primary-600">
              {composeLink.trim()}
            </span>
          </div>
        )}
      </div>

      {/* Collapsed message preview — a compact re-use of the Telegram card so the
          admin sees what goes out without scrolling back to Step 1. */}
      <div className="rounded-xl bg-gray-50 border border-border-primary p-3.5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary mb-1.5">
          Message preview
        </p>
        <div className="font-bold text-sm text-text-primary mb-1 break-words">
          {composeTitle.trim() || 'Title'}
        </div>
        <div className="text-xs text-text-secondary whitespace-pre-wrap leading-snug line-clamp-4">
          {composeMessage.trim() || 'Your message…'}
        </div>
        {composeLink.trim() && (
          <span className="mt-1.5 inline-block font-bold text-xs text-primary-600">
            [{composeLinkLabel.trim() || 'Video Link'}]
          </span>
        )}
      </div>

      {/* Relocated Send Test (R5, Info 7) — present ONLY here. Reuses testChatId /
          handleTestSend / testSending / testResult unchanged. */}
      <div className="rounded-xl bg-gray-50 border border-border-primary p-3.5 space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary">
          Send a test first (optional)
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={testChatId}
            onChange={(e) => setTestChatId(e.target.value)}
            placeholder="Your Telegram chat ID"
            className={`${inputClass} flex-1`}
          />
          <button
            type="button"
            onClick={() => void handleTestSend()}
            disabled={testSending || !testChatId.trim() || !composeMessage.trim()}
            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border-primary text-sm font-medium text-text-secondary hover:bg-gray-50 disabled:opacity-60"
          >
            {testSending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
            Send test
          </button>
        </div>
        <p className="text-xs text-text-secondary">
          Numeric Telegram ID — the person must have started this bot. @usernames only work for public channels.
        </p>
        {testResult && (
          <p className={`text-xs ${testResult.ok ? 'text-green-700' : 'text-red-700'}`}>
            {testResult.message}
          </p>
        )}
      </div>

      {/* Small-send caption (UI-REVIEW observation) — below the typed-confirm
          threshold there is no typed gate, so surface a one-line non-blocking
          reminder of the audience size before the live Send. The abrupt jump from
          Send-Test straight to "Send to N" is most sensitive for small, likely-real
          sends; this softens it without adding friction. */}
      {!requiresTypedConfirm && (
        <p className="text-[13px] text-text-secondary">
          You&apos;re about to send to{' '}
          <span className="font-bold text-text-primary">
            {confirmRecipientCount.toLocaleString()}
          </span>{' '}
          {confirmRecipientCount === 1 ? 'recipient' : 'recipients'}.
        </p>
      )}

      {/* Inline typed-count confirm (sketch .confirm) — replaces the removed
          confirm modal. Shown ONLY for large sends (>= TYPED_CONFIRM_THRESHOLD);
          the footer Send stays disabled until confirmTyped matches the recipient
          count. confirmError is always rendered (Concern 1) so the service-mode
          409 count-mismatch and the "No file selected" upload error surface
          instead of failing silently — even on a small send with no typed box. */}
      {(requiresTypedConfirm || confirmError) && (
        <div className="rounded-xl bg-white border border-border-primary p-3.5">
          {requiresTypedConfirm && (
            <>
              <p className="text-[13px] text-text-secondary mb-2">
                Type{' '}
                <span className="font-bold text-text-primary">{confirmRecipientCount}</span>{' '}
                to confirm you&apos;re sending to all recipients.
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={confirmTyped}
                onChange={(e) => setConfirmTyped(e.target.value)}
                placeholder={String(confirmRecipientCount)}
                className={inputClass}
              />
            </>
          )}
          {confirmError && (
            <div
              className={`${requiresTypedConfirm ? 'mt-3 ' : ''}rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700`}
            >
              {confirmError}
            </div>
          )}
        </div>
      )}
    </>
  );

  // ── Step 1 live Telegram-style preview card (R3) ──────────────────────────
  // Built entirely from the compose state (no new state, no effects) — React
  // re-render makes it live. Mirrors the sketch's `.tg` card: bold title, body,
  // a video thumbnail block for video/YouTube links, and the bracketed [label]
  // (defaulting to "Video Link"). The link row hides entirely with no link.
  const previewHasLink = composeLink.trim().length > 0;
  // Only YouTube links get a native thumbnail in the delivered message; key the
  // preview thumbnail on the same detection so the composer matches reality.
  const previewYouTubeId = previewHasLink ? extractYouTubeId(composeLink) : null;
  const previewLinkLabel = composeLinkLabel.trim() || 'Video Link';
  const step1Preview = (
    <div className="pt-1">
      <p className="text-[11px] font-bold uppercase tracking-wide text-text-secondary mb-2">
        Preview — what recipients get
      </p>
      <div className="bg-gray-50 border border-border-primary rounded-2xl p-3.5 max-w-[88%]">
        {previewYouTubeId && (
          <div className="relative h-40 rounded-xl overflow-hidden bg-gradient-to-br from-gray-300 to-gray-400 mb-2.5">
            {/* Real YouTube thumbnail; the gradient shows through if it fails. */}
            <img
              src={youTubeThumbUrl(previewYouTubeId)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <PlayCircle className="h-11 w-11 text-white/90 drop-shadow" aria-hidden />
            </span>
          </div>
        )}
        <div
          className={`font-bold text-[15px] mb-1 ${
            composeTitle.trim() ? 'text-text-primary' : 'text-gray-400'
          }`}
        >
          {composeTitle.trim() || 'Title'}
        </div>
        <div
          className={`text-sm whitespace-pre-wrap leading-snug ${
            composeMessage.trim() ? 'text-text-secondary' : 'text-gray-400'
          }`}
        >
          {composeMessage.trim() || 'Your message…'}
        </div>
        {previewHasLink && (
          <span className="mt-1.5 inline-block font-bold text-primary-600">
            [{previewLinkLabel}]
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col bg-surface-secondary overflow-hidden">
      {view === 'list' ? renderList() : renderDetail()}

      {/* Compose — full-screen 3-step wizard (Phase 41, replaces the former
          bottom sheet). R1: full-screen page (no bg-black/40 backdrop, no
          rounded-t-2xl drag affordance). R2: Compose·Audience·Review rail.
          R8: fixed header rail + a SINGLE overflow-y-auto body + fixed footer;
          NO nested scroll region inside the body (that broke iOS focus-switch). */}
      {showCompose && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto safe-area-bottom">
          {/* min-h-full flex column: footer sits at the bottom of the screen
              when content is SHORT, and flows after the content (scrolls) when
              it's taller than the screen. Body is the flex-grow spacer — NOT a
              scroll region, so the single-scroll keyboard fix is preserved. */}
          <div className="min-h-full flex flex-col">
          {/* Sticky header: title row (✕) + progress rail. Sticky (not a flex
              sibling) so the wizard is a SINGLE scroll container — the pattern
              that survives the iOS keyboard (a separate middle scroll region
              blanks on field-switch). */}
          <div className="sticky top-0 z-10 bg-white border-b border-border-primary px-5 pb-2.5 safe-area-top">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-text-primary">New Announcement</h2>
              {/* ✕ cancels the whole flow (only meaningful on Step 1; footer Back
                  handles step-to-step nav on Steps 2/3). */}
              <button
                type="button"
                onClick={closeCompose}
                className="p-1.5 -mr-1.5 rounded-full text-text-secondary hover:bg-gray-100 touch-active"
                aria-label="Close"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            {/* Progress rail (R2) — Compose · Audience · Review. active uses the
                filled primary token, done a primary-outline, pending muted. */}
            <div className="flex items-center mt-3.5">
              {([
                { n: 1 as const, label: 'Compose' },
                { n: 2 as const, label: 'Audience' },
                { n: 3 as const, label: 'Review' },
              ]).map(({ n, label }, i) => {
                const active = n === composeStep;
                const done = n < composeStep;
                return (
                  <div key={n} className="contents">
                    {i > 0 && (
                      <div
                        className={`flex-1 h-0.5 mx-1 mb-4 ${
                          n <= composeStep ? 'bg-primary-600' : 'bg-border-primary'
                        }`}
                        aria-hidden
                      />
                    )}
                    <div className="flex flex-col items-center gap-1.5">
                      <div
                        className={`w-[26px] h-[26px] flex items-center justify-center rounded-full text-xs font-bold border-2 ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : done
                              ? 'bg-primary-50 text-primary-600 border-primary-600'
                              : 'bg-gray-100 text-gray-400 border-border-primary'
                        }`}
                      >
                        {n}
                      </div>
                      <span
                        className={`text-[11px] font-semibold ${
                          active
                            ? 'text-primary-700'
                            : done
                              ? 'text-text-secondary'
                              : 'text-gray-400'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Body flows in the SINGLE root scroll container (no nested scroll,
              R8). flex-1 grows to push the footer to the bottom when short. */}
          <div className="flex-1 px-5 py-4">
            {composeStep === 1 && (
              <div className="space-y-4">
                {step1Fields}
                {step1Preview}
              </div>
            )}

            {/* Step 2 — Audience (segmented service/upload + prominent estimate). */}
            {composeStep === 2 && (
              <div className="space-y-4">{step2Audience}</div>
            )}

            {/* Step 3 — Review & Send (summary + Send Test + inline typed-count). */}
            {composeStep === 3 && (
              <div className="space-y-4">{step3Review}</div>
            )}
          </div>

          {/* Footer flows at the end of the scroll content (NOT sticky) — like
              the old FormModal. While typing it sits below the fields (out of
              the way, not floating above the keyboard); scroll to reach it. */}
          <div className="border-t border-border-primary px-5 pt-3.5 pb-6 flex gap-3">
            <button
              type="button"
              onClick={() => {
                if (composeStep === 1) closeCompose();
                else setComposeStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3) : s));
              }}
              className="flex-1 rounded-xl border border-border-primary bg-white px-4 py-3.5 text-base font-semibold text-text-primary hover:bg-gray-50 active:bg-gray-100"
            >
              {composeStep === 1 ? 'Cancel' : '← Back'}
            </button>
            {composeStep < 3 ? (
              <button
                type="button"
                onClick={() => {
                  // Reset the inline typed-count each time we (re)enter Step 3 so
                  // a prior attempt's value never leaks past a recipient change.
                  if (composeStep === 2) {
                    setConfirmTyped('');
                    setConfirmError(null);
                  }
                  setComposeStep((s) => ((s + 1) as 1 | 2 | 3));
                }}
                disabled={
                  // Step 1: Title + Message required.
                  (composeStep === 1 && (!composeTitle.trim() || !composeMessage.trim())) ||
                  // Step 2: recipients>0 (service) / valid matched upload (upload).
                  (composeStep === 2 &&
                    (audienceMode === 'upload'
                      ? !uploadFile || !uploadSummary || uploadSummary.matched === 0
                      : estimatedCount === null || estimatedCount === 0))
                }
                className="flex-1 rounded-xl bg-primary-600 px-4 py-3.5 text-base font-semibold text-white hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            ) : (
              // Step 3 primary — Send. For large sends, disabled until the typed
              // number matches the recipient count; for small sends (below the
              // threshold) it's a plain tap. Also disabled while a create is busy,
              // and at 0 recipients (a stale/failed preview would otherwise enable
              // a "Send to 0" that no-ops silently — handleConfirmSend bails on a
              // null estimate).
              <button
                type="button"
                onClick={() => void handleConfirmSend()}
                disabled={confirmBusy || !step3ConfirmSatisfied || confirmRecipientCount < 1}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3.5 text-base font-semibold text-white hover:bg-primary-700 active:bg-primary-800 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {confirmBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Send to {confirmRecipientCount.toLocaleString()}
              </button>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
